import type {HIDE_OPTIONS} from '@github-ui/commenting/HideCommentActions'
import type {CommentAuthorUserType} from '@github-ui/commenting/ActivityHeader'
import type {ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {CommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerPage'

export interface CommentAuthor {
  login: string
  avatarUrl: string
  /** @deprecated True for every bot (not just AI agents); use `userType` for the agent/bot/human distinction. */
  isAgent?: boolean
  slug?: string
  userType?: CommentAuthorUserType
}

/**
 * Whether `author` is a bot or an AI agent (as opposed to a human). Prefers the
 * real `userType` field; falls back to the legacy `isAgent`/`[bot]`-suffix
 * heuristic for payloads that don't carry `userType` yet (e.g. older cached
 * responses, stories, or hand-built fixtures).
 */
export function isBotOrAgentCommentAuthor(author: CommentAuthor | null): boolean {
  if (author?.userType) return author.userType !== 'human'
  return author?.isAgent === true || author?.login.toLowerCase().endsWith('[bot]') === true
}

export function getCommentAuthorSlug(
  author: CommentAuthor | null,
  isBotOrAgent = isBotOrAgentCommentAuthor(author),
): string | undefined {
  if (author?.slug) return author.slug
  return isBotOrAgent ? author?.login.replace(/\[bot\]$/i, '') : undefined
}

export interface CommentLastContentEdit {
  editedAt: string | null
  editor: CommentAuthor | null
  id: string | null
}

export interface Reactable {
  reactionGroups: ReactionViewerGroup[]
  viewerCanReact: boolean
}

export interface Deletable {
  viewerCanDelete: boolean
}

export interface Minimizable {
  isHidden: boolean
  minimizedReason: HIDE_OPTIONS | null
  viewerCanMinimize: boolean
  viewerCanSeeMinimizeButton: boolean
  viewerCanSeeUnminimizeButton: boolean
}

export interface Updatable {
  viewerCanUpdate: boolean
}

export interface OrgBlockable {
  viewerCanBlockFromOrg: boolean
  viewerCanUnblockFromOrg: boolean
}

export interface PersonalBlockable {
  viewerCanBlockPersonally: boolean
  viewerCanUnblockPersonally: boolean
}

export interface Reportable {
  viewerCanReport: boolean
  viewerCanReportToMaintainer: boolean
  viewerRelationship: string | null
}

export interface CommentBase {
  id: number
  relayId: string
  author: CommentAuthor | null
  authorAssociation: CommentAuthorAssociation
  body: string
  bodyHTML: SafeHTMLString
  bodyVersion: string
  createdAt: string
  lastUserContentEdit: CommentLastContentEdit | null
  publishedAt: string | null
  stafftoolsUrl: string | null
  viewerDidAuthor: boolean
}
